import type {
  FieldSection,
  FieldUIState,
  FieldsState,
  PassEnhancedPreview,
  PosterGenericInput,
  PreviewData,
} from '../../../../store/types/template';
import type { PassItem } from '../../../../store/types/pass';
import { SLOT_ORDER } from '../../pass-templates-new/utils/fieldVisibilityConfig';
import {
  mapFeaturedActionsFromApi,
  type FeaturedActionsApiInput,
} from '../../pass-templates-new/utils/mapApiResponseToTemplateState';
import type { PosterTemplateAttributes } from '../../pass-templates-new/utils/posterGenericInput';
import { PREVIEW_KEY_MAP } from './passEditState';

interface BuildPassEnhancedPreviewParams {
  templateAttributes?: PosterTemplateAttributes;
  templateInput: PosterGenericInput | undefined;
  previewData: PreviewData;
  featuredActions: FeaturedActionsApiInput | null;
}

const NO_STORED_FEATURED_ACTIONS = {};

export const getStoredFeaturedActions = (
  pass: PassItem | undefined
): FeaturedActionsApiInput =>
  pass?.attributes.enhancedData?.generic?.featuredActions ??
  NO_STORED_FEATURED_ACTIONS;

const overrideSection = (
  section: FieldSection,
  slots: Record<string, FieldUIState | null>,
  previewData: PreviewData
) => {
  const previewValues = previewData as unknown as Record<string, unknown>;
  const result: Record<string, FieldUIState | null> = { ...slots };

  SLOT_ORDER.forEach((slot) => {
    const field = slots[slot];
    if (!field) return;
    const keys = PREVIEW_KEY_MAP[`${section}.${slot}`];
    if (!keys) return;
    const value = previewValues[keys.valueKey];
    const label = previewValues[keys.labelKey];
    result[slot] = {
      ...field,
      defaultValue:
        value === undefined ? field.defaultValue : (value as string),
      label: label === undefined ? field.label : (label as string),
    };
  });

  return result;
};

const overrideFields = (
  fields: FieldsState,
  previewData: PreviewData
): FieldsState => ({
  header: overrideSection('header', fields.header, previewData),
  central: overrideSection('central', fields.central, previewData),
  details: overrideSection('details', fields.details, previewData),
  back: overrideSection('back', fields.back, previewData),
});

export const buildPassEnhancedPreview = ({
  templateAttributes,
  templateInput,
  previewData,
  featuredActions,
}: BuildPassEnhancedPreviewParams): PassEnhancedPreview | undefined => {
  const design = templateAttributes?.appleEnhancedDesign;
  if (!templateAttributes || !design?.type) return undefined;

  const hasArtwork = !!design.images?.artwork;

  if (design.type === 'eventTicket') {
    return {
      design: 'eventTicket',
      hasArtwork,
      eventTicketInput: {
        images: {
          artwork: design.images?.artwork ?? '',
          secondaryLogo: design.images?.secondaryLogo ?? '',
        },
        artworkBrightness: design.artworkBrightness ?? null,
      },
    };
  }

  if (!templateInput) return undefined;

  return {
    design: 'generic',
    hasArtwork,
    genericInput: {
      ...templateInput,
      fields: overrideFields(templateInput.fields, previewData),
      barcode: previewData.barcode,
      featuredActions: mapFeaturedActionsFromApi(featuredActions),
    },
  };
};
